<template>
  <info-panel
    v-if="action"
    :item="action"
    :title="action?.title"
    :sections="sections"
    :extra-settings="extraSettings"
    @update:sections="updateSections($event)"
    @update:extra-settings="updateExtraSettings($event)"
    @close="emit('close')"
  >
    <!-- SETTINGS -->
    <template #label-showEmptySections>{{ $t("labels.showEmptySections") }}</template>

    <template #label-image>
      {{ $t("words.image") }}
    </template>
    <template
      #image
      v-if="
        imageData &&
        (extraSettings.image?.value == 'side-full' ||
          extraSettings.image?.value == 'side-cut')
      "
    >
      <img
        :src="imageData"
        :class="{
          'info-panel__image-full': extraSettings.image?.value == 'side-full',
          'info-panel__image-cut': extraSettings.image?.value == 'side-cut'
        }"
      />
    </template>
    <template #option-image-hidden>{{ $t("labels.hidden") }}</template>
    <template #option-image-side-full>{{
      $t("labels.imageAtSideFull")
    }}</template>
    <template #option-image-side-cut>{{
      $t("labels.imageAtSideCut")
    }}</template>

    <template #label-description>
      {{ $t("labels.description") }}
    </template>
    <template #description v-if="settingsValues.showEmptySections || !isEmpty(action.description)">
      <formatted-read-more-text
        :value="action.description"
        :placeholder="$t('errors.noDescription')"
      />
    </template>

    <template #label-costs>
      {{ $t("labels.costs") }}
    </template>
    <template #costs v-if="settingsValues.showEmptySections || action.costs.length > 0">
      <div v-if="action.costs.length > 0" class="line_effect">
        <template v-for="cost in action.costs" :key="cost.elementUid">
          <button-item @click="openOverlay(elements.byUid[cost.elementUid])">
            <span v-if="elements.byUid[cost.elementUid]?.title">
              {{ elements.byUid[cost.elementUid].title }}
            </span>
            <span v-else class="placeholder-text">
              {{ $t("labels.unnamed") }}
            </span>
          </button-item>
          <action-cost-amount :cost="cost" :inline-expression="true" :rounds="rounds" :round-index="selectedRound?.index" />
        </template>
      </div>
      <p v-else class="message mt-1">
        {{ $t("errors.noCosts") }}
      </p>
    </template>

    <!-- EFFECTS -->
    <template #label-effects>
      {{ $t('objectNames.effect', 2) }}
    </template>
    <template #effects v-if="settingsValues.showEmptySections || visibleEffects.length > 0">
      <div v-if="visibleEffects.length > 0" class="line_effect">
        <template v-for="effect in visibleEffects" :key="effect.id">
          <item
            :id="effect.affectedId"
            v-slot="{ item: effectItem }"
            :type="effect.affectedType"
          >
            <button-item @click="openOverlay(effectItem)">
              <span v-if="effectItem.title">{{ effectItem.title }}</span>
              <span v-else class="placeholder-text">
                {{ $t("labels.unnamed") }}
              </span>
            </button-item>
          </item>
          <effect-amount :effect="effect" :inline-expression="true" :rounds="rounds" :round-index="selectedRound?.index" />
        </template>
      </div>
      <p v-else class="message">
        {{ $t("errors.noX", [$t("objectNames.effect", 2)]) }}
      </p>
    </template>

    <template #label-requirements>
      {{ $t('objectNames.requirement', 2) }}
    </template>
    <template #requirements v-if="settingsValues.showEmptySections || action.maxOccurrences != null || requirements.length > 0">
      <div v-if="action.maxOccurrences != null" class="action-infopanel__condition">
        <span class="action-infopanel__conditionstatus">
          <span v-if="selectedRound == null" class="pl-1">•</span>
          <lucide-icon v-else-if="playedMaxAmount" name="x" />
          <lucide-icon v-else name="check" />
        </span>
        <span>
          {{ $t("texts.maxOccurrences", [action.maxOccurrences]) }}
        </span>

        <div
          v-if="action.maxOccurrences != null && requirements.length > 0"
          class="action-infopanel__requirements-separator"
        >
          <strong>{{ $t("words.and") }}</strong>
        </div>
      </div>


      <requirement-list v-if="requirements.length > 0" :item-id="action.id" :item-type="action.type" />

      <p v-else-if="action.maxOccurrences == null" class="message mt-0">
        {{ $t("texts.noRequirements") }}
      </p>
    </template>

    <!-- SCENARIOS -->
    <template #label-scenarios>
      {{ $t("objectNames.scenario", 2) }}
    </template>
    <template #scenarios v-if="settingsValues.showEmptySections || scenarios.length > 0">
      <p>
        {{ $t("texts.scenariosIncludingAction") }}
      </p>
      <button-list :items="scenarios" prop="title" @select="openOverlay" />
    </template>

    <template #label-dependent-incidents>
      {{ $t('objectNames.dependentIncident', 2) }}
    </template>
    <template #dependent-incidents v-if="settingsValues.showEmptySections || dependingIncidents.length > 0">
      <depending-incidents-list :activator="action" @select="openOverlay" />
    </template>

    <!-- STAKEHOLDERS -->
    <template #label-stakeholders>
      {{ $t("objectNames.stakeholder", 2) }}
    </template>
    <template #stakeholders v-if="settingsValues.showEmptySections || stakeholders.length > 0">
      <button-list
        v-if="stakeholders.length > 0"
        :items="stakeholders"
        prop="title"
        @select="openOverlay"
      />
      <p v-else class="message mv-0">
        {{ $t("errors.noX", [$t("objectNames.stakeholder", 2)]) }}
      </p>
    </template>
  </info-panel>
</template>

<script setup lang="ts">
import { computed, reactive } from "vue";
import { useRoute } from "vue-router";
import { toUid } from "@/helpers/utility/Id";
import { isEmpty } from "@/helpers/utility/Text";
import { mapValues } from "@/helpers/utility/CollectionFunctions";

import ButtonList from "@core/ui/ButtonList.vue";
import InfoPanel from "@editor/ui/InfoPanel.vue";
import ButtonItem from "@core/input/ButtonItem.vue";
import Item from "@editor/items/StoreItem.vue";
import EffectAmount from "@editor/items/effect/EffectAmount.vue";
import ActionCostAmount from "@editor/items/action/ActionCostAmount.vue";
import LucideIcon from "@core/ui/LucideIcon.vue";
import FormattedReadMoreText from "@core/ui/FormattedReadMoreText.vue";
import RequirementList from "../requirement/RequirementList.vue";
import DependingIncidentsList from "../incident/DependingIncidentsList.vue";

import { useEffectStore } from "../effect/effects.store";
import { useImageStore } from "@editor/items/image/images.store";
import { useSimulationViewStore } from "@editor/views/simulation/simulationView.store";
import { useStakeholderStore } from "../stakeholder/stakeholders.store";
import { useScenarioStore } from "../scenario/scenarios.store";
import { useRequirementStore } from "../requirement/requirements.store";
import { getDependingIncidents } from "../incident/incidentFunctions";
import { useInfoPanelStore } from "@editor/store/infoPanels.store";
import { useElements } from "@editor/items/element/elements";

import type { OverlayDialogItemRequest } from "@editor/ui/OverlayDialogItem";
import type { ActionItem } from "./ActionItem";
import type { InfoPanelSetting } from "@editor/store/infoPanels.store";
import type { EffectItem } from "../effect/EffectItem";


const props = defineProps<{
  action: ActionItem | null;
}>();
const emit = defineEmits<{
  (e: "close"): void;
  (e: "select", item: OverlayDialogItemRequest): void;
}>();

const route = useRoute();

const imageStore = useImageStore();
const simulationViewStore = useSimulationViewStore();
const effectStore = useEffectStore();
const elements = useElements();

const sections = reactive({
  description: true,
  costs: true,
  effects: true,
  requirements: true,
  "dependent-incidents": true,
  scenarios: false,
  stakeholders: false,
});

const extraSettings = reactive({
  showEmptySections: { type: "check" as const, value: true },
  image: {
    type: "dropdown" as const,
    value: "side-cut",
    options: ["side-full", "side-cut", "hidden"]
  },
});

const infoPanelStore = useInfoPanelStore();

const visibleEffects = computed(() => {
  if (!props.action) return [];
  const effects = effectStore.getByActivator(props.action) || [];
  if (scenario.value) {
    if (scenario.value.effectsVisibility == "hidden") return [];
    if (scenario.value.effectsVisibility == "visible") return effects.map((effect): EffectItem => ({ ...effect, visibility: "visible" }));
    if (scenario.value.effectsVisibility == "values-hidden") return effects.map((effect): EffectItem => ({...effect, visibility: "hint" }));
  }
  if (props.action?.effectsVisibility == "hidden") return [];
  if (props.action?.effectsVisibility == "visible") return effects.map((effect): EffectItem => ({ ...effect, visibility: "visible" }));
  if (props.action?.effectsVisibility == "values-hidden") return effects.map((effect): EffectItem => ({...effect, visibility: "hint" }));
  return effects.filter((effect) => effect.visibility !== "hidden");
});

const scenarios = computed(() => {
  if (props.action == null) return [];
  return useScenarioStore().getByAction(props.action.id);
});

const scenario = computed(() => {
  if (!["simulation", "inspector", "evaluation"].includes(route?.meta?.areaName as string)) return null;
  return simulationViewStore.scenario;
});

const rounds = computed(() => {
  if (!["simulation", "inspector", "evaluation"].includes(route?.meta?.areaName as string)) return undefined;
  return simulationViewStore.rounds;
});
const selectedRound = computed(() => {
  if (!["simulation", "inspector", "evaluation"].includes(route?.meta?.areaName as string)) return null;
  return simulationViewStore.selectedRound;
});
const currentRound = computed(() => {
  return simulationViewStore.currentRound;
});

// ===========================
// Conditions

const playedMaxAmount = computed(() => {
  if (currentRound.value == null) return false;
  if (props.action?.maxOccurrences == null) return false;
  return (
    props.action.maxOccurrences <=
    currentRound.value.actionCounts[props.action.id]
  );
});

// ===========================
// Requirements

const requirements = computed(() => {
  if (!props.action) return [];
  return useRequirementStore().byItemUid[toUid("action", props.action.id)] ?? [];
});

// ===========================
// IMAGE

const image = computed(() => {
  if (props.action && props.action.imageId != null) {
    return imageStore.items[props.action.imageId];
  }
  return null;
});

const imageData = computed(() => {
  if (!image.value) return undefined;
  return imageStore.mediaData[image.value.id] ?? undefined;
});

// ===========================
// STAKEHOLDERS

const stakeholders = computed(() => {
  if (props.action == null) return [];
  return useStakeholderStore().byAction[props.action.id] ?? [];
});

// ===========================
// DEPENDING INCIDENTS

const dependingIncidents = computed(() => {
  if (!props.action) return [];
  return getDependingIncidents(props.action);
});

function openOverlay(item: OverlayDialogItemRequest) {
  emit("select", item);
}


function updateSections(value: { [section: string]: boolean }) {
  Object.assign(sections, value);
  infoPanelStore.sections.action = sections;
}

function loadSections() {
  const savedSections = infoPanelStore.sections?.action || {};
  for (const key in sections) {
    if (key in savedSections) {
      sections[key as (keyof typeof sections)] = savedSections[key];
    }
  }
}

loadSections();


function updateExtraSettings(value: { [setting: string]: InfoPanelSetting }) {
  Object.assign(extraSettings, value);
  infoPanelStore.extraSettings.action = extraSettings;
}

function loadExtraSettings() {
  const savedExtraSettings = infoPanelStore.extraSettings?.action || {};
  for (const key in extraSettings) {
    if (key in savedExtraSettings) {
      extraSettings[key as (keyof typeof extraSettings)] = { ...(savedExtraSettings[key] as any) };
    }
  }
}

const settingsValues = computed(() => {
  return mapValues(extraSettings, (setting) => setting.value);
});

loadExtraSettings();
</script>

<style lang="scss">
.action-infopanel__condition {
  display: grid;
  align-items: center;
  gap: 0.5rem;
  width: max-content;
  max-width: 100%;
  grid-template-columns: 1.25rem minmax(0, 1fr);
  margin-top: 1rem;
  margin-bottom: 0.5rem;
}

.action-infopanel__requirements-separator {
  grid-column: span 2;
  margin-top: 0.25rem;
  padding-top: 0.25rem;
  padding-bottom: 0.25rem;
  margin-bottom: 0.5rem;
  text-align: left;
  font-size: 0.9rem;
  color: var(--color-text-medium);
  font-weight: bold;
  // padding-left: 0.5rem;

  background-image: linear-gradient(
    to right,
    var(--color-grey) 0%,
    var(--color-grey) 100%
  );

  // create a thin horizontal line,
  // separating the box vertically,
  // with a gap for the word "or" to the left
  // background-image: linear-gradient(
  //   to right,
  //   var(--color-grey) 0%,
  //   var(--color-grey) 0.25rem,
  //   transparent 0.25rem,
  //   transparent 2.5rem,
  //   var(--color-grey) 2.5rem,
  //   var(--color-grey) 100%
  // );
  background-position: center;
  background-size: 100% 1px;
  background-repeat: no-repeat;

  strong {
    display: none;
  }
}
.action-infopanel__conditionstatus {
  color: var(--color-text);
  font-size: 16px;
  margin-top: 3px;
  margin-left: -1px;
}
</style>
